/**
 * QuickStartScreen2 - Game Search & Collection Add
 *
 * Second screen of the Quick Start onboarding flow.
 * Allows users to search for games and add them to their collection with an
 * independent shelf-status + "Played" toggle, matching AddGameModal's model:
 * ownership_status (owned/interested/excluded, or none) is decoupled from
 * has_played, so combinations like "owned AND played" are representable.
 */

import React, { useState, useEffect } from "react";
import { API_BASE } from "../../config/api";
import GameSearchDropdown from "../common/GameSearchDropdown";

// Reuse the same shelf options/labels as AddGameModal for consistency.
const OWNERSHIP_OPTIONS = [
  { value: "owned", label: "I have this" },
  { value: "interested", label: "Interested" },
  { value: "excluded", label: "Not for me" },
];

// Reuse preference options from PreferenceIcons
const PREFERENCE_OPTIONS = [
  { value: "like_a_lot", label: "Love", color: "#2e7d32" },
  { value: "like", label: "Like", color: "#4caf50" },
  { value: "neutral", label: "Neutral", color: "#757575" },
  { value: "dislike", label: "Dislike", color: "#c62828" },
];

const QuickStartScreen2 = ({
  selectedGames,
  onAddGame,
  onRemoveGame,
  onUpdateOwnership,
  onUpdateHasPlayed,
  onUpdatePreference,
  onBulkAdd,
  onSkip,
  onBack,
  isLoading,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Search games when query changes
  useEffect(() => {
    const searchGames = async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetch(
          `${API_BASE}/games/search?q=${encodeURIComponent(searchQuery)}&limit=15`
        );
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.games || []);
        }
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchGames, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelectGame = (game) => {
    // No shelf status by default; has_played defaults to true (matches
    // AddGameModal's fresh-add default / the no_shelf catch-all).
    onAddGame(game, null, "neutral");
    setSearchQuery("");
    setShowDropdown(false);
  };

  return (
    <div className="game-selection-screen">
      <div className="game-selection-columns">
        {/* Left column - Search */}
        <div className="game-selection-col">
          <h4>Add Games</h4>

          <GameSearchDropdown
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isVisible={showDropdown}
            onVisibilityChange={setShowDropdown}
            searchResults={searchResults}
            isLoading={isSearching}
            onGameSelect={handleSelectGame}
            showImages={true}
            placeholder="Search for games to add..."
          />

          <p style={{ fontSize: "0.875rem", color: "var(--text-secondary, #666)" }}>
            Search for games you own or have played. You can add up to 20 games to get started.
          </p>
        </div>

        {/* Right column - Selected games */}
        <div className="game-selection-col">
          <h4>Selected Games ({selectedGames.length})</h4>

          <div className="selected-games-list">
            {selectedGames.length === 0 ? (
              <div className="empty-selection">
                No games selected yet.
                <br />
                Search and add games from the search bar.
              </div>
            ) : (
              selectedGames.map((game) => (
                <div key={game.id} className="selected-game-item">
                  <div className="selected-game-header">
                    {game.thumbnail && (
                      <img
                        src={game.thumbnail}
                        alt={game.name}
                        className="selected-game-thumb"
                      />
                    )}
                    <div className="selected-game-info">
                      <div className="selected-game-name">{game.name}</div>
                    </div>
                    <button
                      className="selected-game-remove"
                      onClick={() => onRemoveGame(game.id)}
                      aria-label={`Remove ${game.name}`}
                    >
                      ×
                    </button>
                  </div>

                  <div className="game-config-row" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.375rem" }}>
                    <span style={{ fontSize: "0.75rem", fontWeight: "bold", color: "var(--text-primary, #333)" }}>
                      Shelf category
                    </span>
                    <button
                      type="button"
                      onClick={() => onUpdateHasPlayed(game.id, !game.hasPlayed)}
                      disabled={isLoading || (game.hasPlayed && !game.ownershipStatus)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        cursor: (isLoading || (game.hasPlayed && !game.ownershipStatus)) ? "not-allowed" : "pointer",
                        border: game.hasPlayed
                          ? "1px solid var(--pista-green-primary, #2E7D32)"
                          : "1px dashed var(--border-color, #aaa)",
                        backgroundColor: game.hasPlayed
                          ? "var(--pista-green-primary, #2E7D32)"
                          : "var(--bg-secondary, #e0e0e0)",
                        color: game.hasPlayed ? "white" : "var(--text-primary, #333)",
                      }}
                    >
                      {game.hasPlayed ? "Played ✕" : "+ Played"}
                    </button>
                  </div>

                  <div className="game-config-row" style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    {OWNERSHIP_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => onUpdateOwnership(game.id, opt.value)}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: "0.375rem",
                          border: `1px solid ${
                            game.ownershipStatus === opt.value
                              ? "var(--pista-green-primary, #2E7D32)"
                              : "var(--border-color, #ddd)"
                          }`,
                          borderRadius: "6px",
                          fontSize: "0.75rem",
                          backgroundColor:
                            game.ownershipStatus === opt.value
                              ? "var(--pista-green-primary, #2E7D32)"
                              : "var(--bg-secondary, white)",
                          color: game.ownershipStatus === opt.value ? "white" : "var(--text-primary, #333)",
                          cursor: isLoading ? "not-allowed" : "pointer",
                          fontWeight: game.ownershipStatus === opt.value ? "bold" : "normal",
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="game-config-row">
                    <select
                      value={game.preference}
                      onChange={(e) => onUpdatePreference(game.id, e.target.value)}
                      className="game-config-select"
                    >
                      {PREFERENCE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="quick-start-footer" style={{ marginTop: "1rem" }}>
        <div className="quick-start-footer-left">
          <button
            className="btn btn-secondary"
            onClick={onBack}
            disabled={isLoading}
          >
            ← Back
          </button>
          <button
            className="btn btn-text"
            onClick={onSkip}
            disabled={isLoading}
          >
            Skip this step
          </button>
        </div>
        <div className="quick-start-footer-right">
          <button
            className="btn btn-primary"
            onClick={onBulkAdd}
            disabled={selectedGames.length === 0 || isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                Adding...
              </>
            ) : (
              `Add ${selectedGames.length} Game${selectedGames.length !== 1 ? "s" : ""} to Collection →`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickStartScreen2;
